import { useMemo } from 'react';
import { FilePen, ListTodo } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { ActivityBlock } from '../components/ActivityBlock';
import { HomeSection } from '../components/HomeSection';
import { HomeShortcuts, type Shortcut } from '../components/HomeShortcuts';
import { useHomeDensity, useHomePipeline, useHomeVolume } from '../hooks/useHome';
import {
  MONTHS_BACK,
  ministryActivity,
  monthColumns,
  pipelineStats,
  weekBuckets,
} from '../lib/buckets';
import { useAuth } from '@/app/providers/AuthProvider';
import { useMinistriesWithLeaders, useProjectStatusCounts } from '@/features/org';
import { DRAFTS_IN_CALENDAR_PATH, ProjectTable, usePendingProjects } from '@/features/projects';
import { useReviewQueue } from '@/features/review';
import { ColumnChart } from '@/shared/components/chart/ColumnChart';
import { DensityStrip } from '@/shared/components/chart/DensityStrip';
import { Metric } from '@/shared/components/chart/Metric';
import { StatusBar } from '@/shared/components/chart/StatusBar';
import { EmptyState } from '@/shared/components/EmptyState';
import { LoadingState } from '@/shared/components/LoadingState';
import { PageHeader } from '@/shared/components/PageHeader';
import { messages } from '@/shared/constants/messages';
import { can } from '@/shared/constants/roles';
import { CALENDAR_STATUSES, WAITING_DAYS_ALERT_THRESHOLD } from '@/shared/constants/statuses';
import { daysSince, formatMonthLabel } from '@/shared/lib/dates';
import { toUserMessage } from '@/shared/lib/errors';
import { formatNumber, pluralize } from '@/shared/lib/format';
import { aggregateStatusCounts } from '@/shared/lib/statusCounts';
import type { ProjectStatus, StatusCounts } from '@/shared/types/domain';

/**
 * A Início: **o que está acontecendo no seu escopo**, em números e gráficos.
 *
 * Ela era uma tela de lista com cabeçalho — saudação, cinco tiles, um
 * `ProjectTable` e a estrutura da igreja —, e cada uma dessas partes era feita
 * melhor por outra aba: a tabela por `/projetos`, a fila por `/analise`, a
 * estrutura por `/coordenacoes`. Agora ela responde as perguntas que nenhuma
 * das outras responde, porque nenhuma delas olha para o TEMPO: estou
 * acelerando ou parando, onde a igreja já está cheia, a esteira está andando.
 *
 * ── A regra que governa esta tela ──────────────────────────────────────────
 *
 * **A Início só afirma sobre o alcance de quem olha, e diz isso em palavras.**
 *
 * Toda contagem daqui chega recortada pela RLS (`projects_select` recorta por
 * `my_ministry_ids()`, com a estreitagem que esconde rascunho alheio). Isso
 * derrubou a US-11 da tela de coordenação em 21/08: lá a tela NOMEAVA uma
 * coordenação e entregava o recorte de quem olhava. Aqui a moldura já é "você",
 * então o mesmo recorte deixa de ser mentira e vira o assunto — mas só enquanto
 * cada bloco disser de quem é o número, que é o que `HomeSection.caption`
 * obriga.
 *
 * ⚠️ **Um bloco foge do alcance, e de propósito:** a densidade das próximas
 * semanas mostra a FILIAL inteira, para os cinco papéis, pela RPC
 * `agenda_projects` ([ADR-015](../../../../docs/adr/ADR-015-agenda-volatil.md)).
 * Contar o que a pessoa já vê na grade do Calendário é estritamente menos
 * informação do que a grade. Por isso a legenda dele diz "na igreja" enquanto
 * as outras dizem "no seu alcance".
 *
 * ── Composição por papel ───────────────────────────────────────────────────
 *
 * **Papel REMOVE bloco, nunca renomeia nem reordena** — a regra 3 da ADR-012,
 * aplicada ao conteúdo em vez da navegação. Todo mundo vê os quatro primeiros;
 * `reviewProject` ganha a saúde da esteira, `readOwnCoordination` ganha onde
 * está a atividade.
 *
 * ⚠️ O gate de "onde está a atividade" **não é preferência, é correção**: aquele
 * bloco cruza duas fontes de escopos diferentes, que só coincidem para quem
 * enxerga a coordenação inteira. Ver o docblock de `ministryActivity`, e o teste
 * que prende os dois.
 */
export function HomePage() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  // Um instante só para a tela inteira. Dois `new Date()` em blocos diferentes
  // podem cair em lados opostos da meia-noite e produzir uma tela que discorda
  // de si mesma — a coluna "agora" num mês e a primeira semana em outro.
  const now = useMemo(() => new Date(), []);

  const roles = currentUser?.roles ?? [];
  const isReviewer = can(roles, 'reviewProject');
  const seesCoordination = can(roles, 'readOwnCoordination');

  const countsQuery = useProjectStatusCounts();
  const volumeQuery = useHomeVolume(now);
  const densityQuery = useHomeDensity(now);
  const pipelineQuery = useHomePipeline(now, isReviewer);
  const pendingQuery = usePendingProjects();
  const reviewQueue = useReviewQueue(isReviewer);
  const ministriesQuery = useMinistriesWithLeaders();

  const error = countsQuery.error ?? volumeQuery.error ?? densityQuery.error;
  if (error) {
    return <EmptyState title="Não foi possível carregar" description={toUserMessage(error)} />;
  }

  if (!currentUser) return null; // RequireAuth já garante isto — só acalma o TS.

  const counts = countsQuery.data;
  const volume = volumeQuery.data;
  if (!counts || !volume) {
    return <LoadingState label="Carregando seu painel…" />;
  }

  // Nome inteiro, e não o primeiro: a saudação é o único lugar do painel que
  // diz para quem a tela está falando, e num sistema de 150 voluntários há mais
  // de um Pedro.
  const fullName = currentUser.profile.full_name.trim();

  /**
   * ── Atalhos: cada número abre a tela que o produz, e nenhum é uma soma ────
   *
   * ⚠️ **A fila de análise NÃO tem atalho aqui.** Ela já tem item próprio na
   * sidebar, com contador, para quem tem `reviewProject` — repeti-la como alerta
   * na Início cobrava atenção duas vezes pela mesma pendência, na mesma tela em
   * que o bloco "Saúde da esteira" já diz quantos estão parados. Sobram os dois
   * atalhos que não têm outro lugar: rascunho e projeto devolvido.
   */
  const pending = pendingQuery.data ?? [];
  const drafts = pending.filter((project) => project.status === 'draft').length;
  const adjustments = pending.filter((project) => project.status === 'changes_requested').length;

  const shortcuts: Shortcut[] = [
    ...(adjustments > 0
      ? [
          {
            key: 'adjustments',
            label: messages.home.adjustmentsShortcut(adjustments),
            // A caixa de entrada mora no Calendário (ADR-015 §6) — este atalho
            // LEVA até ela, não a reimplementa.
            to: '/calendario',
            icon: ListTodo,
            tone: 'attention' as const,
          },
        ]
      : []),
    ...(drafts > 0
      ? [
          {
            key: 'drafts',
            label: messages.home.draftsShortcut(drafts),
            to: DRAFTS_IN_CALENDAR_PATH,
            icon: FilePen,
            tone: 'neutral' as const,
          },
        ]
      : []),
  ];

  // ── Os blocos de volume, todos da mesma consulta de dez meses ─────────────
  const aggregated = aggregateStatusCounts(counts);
  /**
   * ⚠️ **O rascunho sai da barra, e a razão apareceu ao olhar a tela.**
   *
   * Duas coisas se somaram. A primeira é visual: `draft` e `completed`
   * compartilham o tom `neutral` (`PROJECT_STATUS_TONES`) — o que é inofensivo
   * num crachá, onde nunca aparecem lado a lado, e ambíguo numa barra
   * empilhada, que nascia cinza numa ponta e cinza na outra sem nada dizendo
   * qual era qual.
   *
   * A segunda é de coerência, e é a que decide: `fetchHomeVolume` já exclui
   * `draft`, então o gráfico de volume ao lado nunca o contou. Uma barra que
   * conta 9 sobre um gráfico que conta 8 é a tela discordando de si mesma.
   *
   * A regra que fica: **os gráficos da Início são sobre a ESTEIRA; o rascunho é
   * o atalho.** Ele não sumiu da tela — ele está na primeira linha, com verbo e
   * destino próprios, que é onde ele pede ação.
   */
  const inPipeline = { ...aggregated, draft: undefined };
  const columns = monthColumns(volume, now);
  const currentColumn = columns.find((column) => column.current);
  const previousColumn = columns[MONTHS_BACK - 1];
  const buckets = weekBuckets(densityQuery.data ?? [], now);

  /**
   * ⚠️ **`rejected` e `cancelled` ficam de fora, e o defeito apareceu na tela.**
   *
   * A primeira versão filtrava só por data, e um projeto rejeitado com data
   * futura aparecia sob o título "O que vem por aí" — dizendo que vai acontecer
   * o que a liderança decidiu que não acontece. É o mesmo erro de sentido
   * invertido que a regra 8 do CLAUDE.md nomeia para o calendário: os dois
   * estados terminais negativos diriam "ocupado" sobre espaço livre.
   *
   * `CALENDAR_STATUSES` é a lista certa e não uma nova: ela já é o vocabulário
   * de "o que está vivo e tem data" no resto do painel. `changes_requested` fica
   * fora dela de propósito — está com o ministério, e para quem é dono ele já
   * chegou pelo atalho, com verbo.
   */
  const upNext = volume
    .filter(
      (project) =>
        Date.parse(project.starts_at) >= now.getTime() &&
        (CALENDAR_STATUSES as readonly ProjectStatus[]).includes(project.status),
    )
    .slice(0, 5);

  const activity = ministryActivity(volume, ministriesQuery.data ?? []);

  const stats = pipelineStats(pipelineQuery.data ?? []);
  const stalled = (reviewQueue.data ?? []).filter(
    (item) =>
      item.submitted_at && daysSince(item.submitted_at, now) > WAITING_DAYS_ALERT_THRESHOLD,
  ).length;

  return (
    <div className="space-y-6">
      <PageHeader
        title={`Olá, ${fullName}`}
        description={scopeSentence(seesCoordination, isReviewer)}
      />

      <HomeShortcuts shortcuts={shortcuts} />

      <HomeSection
        title={messages.home.statusTitle}
        caption={messages.home.statusCaption}
        /*
          ⚠️ Para líder e auxiliar o destino é o Calendário, não `/projetos`: a
          decisão 97 tirou aquela aba do menu deles (ADR-015 §6 — o ciclo inteiro
          cabe no Calendário), e mandá-los para lá daqui reabriria pela Início a
          porta que a navegação fechou de propósito. A rota continua existindo
          para todos; o que muda é para onde ESTA tela empurra.
        */
        action={
          seesCoordination
            ? { label: 'Ver projetos', to: '/projetos' }
            : { label: 'Ver calendário', to: '/calendario' }
        }
      >
        <StatusBar counts={inPipeline} scopeLabel={messages.home.scopeReach} />
      </HomeSection>

      <div className="grid gap-6 lg:grid-cols-2">
        <HomeSection title={messages.home.volumeTitle} caption={messages.home.volumeCaption}>
          <ColumnChart data={columns} />
          <p className="mt-4 text-sm text-muted-foreground">
            {comparisonSentence(currentColumn?.counts, previousColumn?.counts, now)}
          </p>
        </HomeSection>

        <HomeSection
          title={messages.home.densityTitle}
          caption={messages.home.densityCaption}
          action={{ label: 'Abrir calendário', to: '/calendario' }}
        >
          <DensityStrip
            buckets={buckets}
            onSelect={(bucket) => navigate(`/calendario?data=${bucket.to}`)}
          />
        </HomeSection>
      </div>

      {isReviewer ? (
        <HomeSection
          title={messages.home.pipelineTitle}
          caption={messages.home.pipelineCaption}
          action={{ label: 'Abrir fila', to: '/analise' }}
        >
          <div className="grid gap-3 sm:grid-cols-3">
            <Metric
              label={messages.home.medianLabel}
              value={
                stats.medianDays === null
                  ? '—'
                  : pluralize(Math.round(stats.medianDays), 'dia', 'dias')
              }
              hint={stats.medianDays === null ? messages.home.medianEmpty : messages.home.medianHint}
              tone={stats.medianDays !== null && stats.medianDays > 7 ? 'attention' : 'neutral'}
            />
            <Metric
              label={messages.home.stalledLabel}
              value={formatNumber(stalled)}
              hint={messages.home.stalledHint(WAITING_DAYS_ALERT_THRESHOLD)}
              tone={stalled > 0 ? 'attention' : 'neutral'}
            />
            <Metric
              label={messages.home.decidedLabel}
              value={formatNumber(stats.decidedCount)}
            />
          </div>
        </HomeSection>
      ) : null}

      <HomeSection
        title={messages.home.upNextTitle}
        caption={messages.home.upNextCaption}
        action={{ label: 'Ver calendário', to: '/calendario' }}
      >
        <ProjectTable
          projects={upNext}
          showMinistry={seesCoordination}
          emptyTitle={messages.empty.home}
          emptyDescription={messages.home.emptyHint}
        />
      </HomeSection>

      {seesCoordination ? (
        <HomeSection
          title={messages.home.activityTitle}
          caption={messages.home.activityCaption}
          action={{ label: 'Ver coordenações', to: '/coordenacoes' }}
        >
          <ActivityBlock ranked={activity.ranked} silent={activity.silent} />
        </HomeSection>
      ) : null}
    </div>
  );
}

/**
 * A frase do cabeçalho: **até onde vai o que esta tela mostra** — e só isso.
 *
 * ⚠️ **Ela não carrega mais um total, e a razão apareceu ao olhar a tela.** A
 * primeira versão dizia "19 projetos" logo acima de uma barra que somava 17: o
 * cabeçalho contava com `totalProjects` (os sete status) e a barra com
 * `COUNT_GROUPS`, que **não tem grupo para `rejected` nem `cancelled`** — para
 * quem varre um panorama, os dois significam "não vai acontecer"
 * (`statusCounts.ts`). Nenhum dos dois números estava errado; errado era a tela
 * exibir os dois como se fossem o mesmo.
 *
 * A regra que fica: **um número, um lugar.** Quem conta é o bloco que mostra a
 * composição daquela contagem; o cabeçalho declara a moldura, que é o trabalho
 * dele desde o começo.
 */
function scopeSentence(seesCoordination: boolean, isReviewer: boolean): string {
  const reach = isReviewer
    ? 'a filial inteira'
    : seesCoordination
      ? 'a sua coordenação'
      : 'o seu ministério';
  return `Os números desta tela cobrem ${reach}.`;
}

/**
 * "12 projetos em agosto — 4 a mais que no mês anterior."
 *
 * ⚠️ **Cor nunca é o único portador** (US-14): esta frase é o que faz a
 * comparação do gráfico chegar a quem não distingue a altura de duas colunas
 * vizinhas — e a quem está lendo por leitor de tela.
 *
 * Sem mês anterior na janela, ela não inventa comparação: diz só o número do
 * mês corrente. É o caso da primeira coluna do eixo.
 */
function comparisonSentence(
  current: StatusCounts | undefined,
  previous: StatusCounts | undefined,
  now: Date,
): string {
  const sum = (counts: StatusCounts | undefined) =>
    Object.values(counts ?? {}).reduce((total, value) => total + (value ?? 0), 0);

  const currentTotal = sum(current);
  const month = formatMonthLabel(now).replace(/ de \d{4}$/, '');
  const head = `${pluralize(currentTotal, 'projeto', 'projetos')} em ${month}`;

  if (!previous) return `${head}.`;

  const delta = currentTotal - sum(previous);
  if (delta === 0) return `${head} — o mesmo do mês anterior.`;

  const direction = delta > 0 ? 'a mais' : 'a menos';
  return `${head} — ${formatNumber(Math.abs(delta))} ${direction} que no mês anterior.`;
}
